import type { PriceOverrideSort, PriceOverrideStatus, SortOrder } from "@classes-service-types/index";
import { PriceOverrideStatus as PriceOverrideStatusOptions } from "@classes-service-types/index";
import { isEqual as dateEqual, format as formatDate, isValid } from "date-fns";
import { isEqual } from "lodash";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ALL_PRICE_OVERRIDE_STATUSES_SORTED } from "@/components/OperationsApp/components/PriceOverrideHeader/constants.ts";
import { PRICE_OVERRIDE_API_DATE_FORMAT } from "@/constants.ts";

export const useFilters = () => {
  const route = useRoute();
  const router = useRouter();

  const status = ref<Array<PriceOverrideStatus> | undefined>(undefined);
  const orderStatus = ref<Array<string>>([]);
  const session = ref<Array<string>>([]);
  const city = ref<Array<number>>([]);

  watch(
    () => route.query,
    async (newParams, oldParams) => {
      if (newParams !== oldParams) {
        if (newParams.status && newParams.status !== status.value) {
          status.value = Array.isArray(newParams.status)
            ? (newParams.status as PriceOverrideStatus[])
            : [newParams.status as PriceOverrideStatus];
        } else {
          status.value = [PriceOverrideStatusOptions.NeedsReview];
          await router.replace({
            name: "price-override-approvals",
            query: {
              ...route.query,
              status: PriceOverrideStatusOptions.NeedsReview,
            },
          });
        }

        let newOrderStatus = newParams.orderStatus ? newParams.orderStatus : [];
        newOrderStatus = Array.isArray(newOrderStatus) ? newOrderStatus : [newOrderStatus];
        if (!isEqual(newOrderStatus.sort(), orderStatus.value.sort())) {
          orderStatus.value = newOrderStatus.map((value) => `${value}`).filter((val) => !!val);
        }

        let newSession = newParams.session ? newParams.session : [];
        newSession = Array.isArray(newSession) ? newSession : [newSession];
        if (!isEqual(newSession.sort(), session.value.sort())) {
          session.value = newSession.map((value) => `${value}`).filter((val) => !!val);
        }

        let newCity = newParams.city ? newParams.city : [];
        newCity = Array.isArray(newCity) ? newCity : [newCity];
        if (!isEqual(newCity.sort(), city.value.sort())) {
          city.value = newCity.map((value) => Number(value)).filter((val) => !!val);
        }
      }
    },
    { immediate: true },
  );

  const isAllStatus = computed(() => {
    return status.value && Array.isArray(status.value)
      ? ALL_PRICE_OVERRIDE_STATUSES_SORTED.every((v) => status?.value?.includes(v))
      : false;
  });

  const updateStatus = async (updatedStatus: PriceOverrideStatus) => {
    status.value = [updatedStatus];
    await router.push({
      query: {
        ...route.query,
        status: updatedStatus,
      },
    });
  };

  const updateOrderStatus = async (updatedOrderStatus: Array<string>) => {
    orderStatus.value = updatedOrderStatus;
    await router.push({
      query: {
        ...route.query,
        orderStatus: updatedOrderStatus,
      },
    });
  };

  const updateSession = async (updatedSession: Array<string>) => {
    session.value = updatedSession;
    await router.push({
      query: {
        ...route.query,
        session: updatedSession,
      },
    });
  };

  const updateCity = async (updatedCity: Array<number>) => {
    city.value = updatedCity;
    await router.push({
      query: {
        ...route.query,
        city: updatedCity,
      },
    });
  };

  const clearFilters = async () => {
    await router.push({
      query: {},
    });
  };

  return {
    status,
    updateStatus,
    orderStatus,
    updateOrderStatus,
    session,
    updateSession,
    city,
    updateCity,
    clearFilters,
    isAllStatus,
  };
};
